import type { MultiPayload } from "@defuse-protocol/contract-types"
import { logger } from "@src/utils/logger"
import { assign, fromPromise, setup } from "xstate"

/** Signed NEP-413 payload preserved after auth failure so the parent can
 * retry once `add_public_key` lands. */
export interface FailedNearSignature {
  accountId: string
  publicKey: string
  signedData: MultiPayload
}

export interface VerificationResult {
  success: boolean
  token?: string
  expiresAt?: number
  failedNearSignature?: FailedNearSignature
}

export interface WalletVerificationOutput {
  verified: boolean
  failedNearSignature: FailedNearSignature | null
}

export const walletVerificationMachine = setup({
  types: {} as {
    context: {
      hadError: boolean
      verificationResult: VerificationResult | null
    }
    output: WalletVerificationOutput
  },
  actors: {
    verifyWallet: fromPromise((): Promise<VerificationResult> => {
      throw new Error("not implemented")
    }),
  },
  delays: {
    VERIFICATION_TIMEOUT: 60_000,
  },
  actions: {
    logError: (_, { error }: { error: unknown }) => {
      logger.error(error)
    },
    setError: assign({
      hadError: (_, { hadError }: { hadError: true }) => hadError,
    }),
    clearError: assign({ hadError: false }),
    setVerificationResult: assign({
      verificationResult: (
        _,
        { result }: { result: VerificationResult | null }
      ) => result,
    }),
  },
  guards: {
    isVerified: (_, result: VerificationResult) => result.success === true,
    hasFailedNearSignature: (_, result: VerificationResult) =>
      result.success === false && result.failedNearSignature != null,
  },
}).createMachine({
  id: "verify-wallet",
  initial: "idle",
  context: {
    hadError: false,
    verificationResult: null,
  },
  output: ({ context }): WalletVerificationOutput => ({
    verified: context.verificationResult?.success === true,
    failedNearSignature:
      context.verificationResult?.failedNearSignature ?? null,
  }),
  states: {
    idle: {
      on: {
        START: {
          target: "verifying",
          actions: "clearError",
        },
        ABORT: "aborted",
      },
    },
    verifying: {
      on: {
        ABORT: "aborted",
      },
      // Timeout returns to `idle` (non-final) so the dialog shows retry/cancel.
      // Parent only progresses on `onDone` — do not auto-bubble or the user
      // is stranded with no recovery.
      after: {
        VERIFICATION_TIMEOUT: {
          target: "idle",
          actions: [
            {
              type: "logError",
              params: { error: "Verification timed out after 60s" },
            },
            {
              type: "setError",
              params: { hadError: true },
            },
          ],
        },
      },
      invoke: {
        src: "verifyWallet",
        onDone: [
          {
            target: "verified",
            guard: {
              type: "isVerified",
              params: ({ event }) => event.output,
            },
            actions: {
              type: "setVerificationResult",
              params: ({ event }) => ({ result: event.output }),
            },
          },
          {
            target: "failed_pubkey_missing",
            guard: {
              type: "hasFailedNearSignature",
              params: ({ event }) => event.output,
            },
            actions: {
              type: "setVerificationResult",
              params: ({ event }) => ({ result: event.output }),
            },
          },
          {
            target: "idle",
            actions: [
              {
                type: "logError",
                params: {
                  error: "Wallet produced an unverifiable signature",
                },
              },
              {
                type: "setError",
                params: { hadError: true },
              },
            ],
          },
        ],
        onError: {
          target: "idle",
          actions: [
            {
              type: "logError",
              params: ({ event }) => event,
            },
            {
              type: "setError",
              params: { hadError: true },
            },
          ],
        },
      },
    },
    verified: {
      type: "final",
    },
    failed_pubkey_missing: {
      type: "final",
    },
    aborted: {
      type: "final",
    },
  },
})
